import { MemberTimeProvider } from 'lowcode/utils/context/memberTimeZone';
import Head from 'next/head';
import MemberGloData from 'lowcode/utils/MemberGloData';
import Product from 'lowcode/pages/product';
import { EcoProvider } from 'lowcode/utils/context/ecocredits';
import { queryEditorDetailOnline } from 'lowcode/utils/service';
import * as LowcompIndex from 'lowcomp/plugins/index-series/indexS';
import * as LowcompMember from 'lowcomp/plugins/member/indexS';
import type { GetServerSidePropsContext, NextPage } from 'next';
import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/app/utils/useLang';
import EcoCreditsCompoment from '@/components/EcoCredits';
import { MonthlyProvider } from '@/components/EcocreditsMonthlyMadness/MonthlyProvider';
import { IPageStatus } from '@/components/EcocreditsMonthlyMadness/type';
import PageLayout from '@/components/PageLayout';
import SEOlinksA from '@/components/SEOlinksA';
import seoconfig from '@/seoconfig/ecocredits';
import { redirectTo404 } from '@/utils';
import efBridge from '@/utils/JSBridge';
import { onLogin } from '@/utils/cookie';
import { useGlobal } from '@/utils/useGlobal';
import './index.scss';
import { getAppSession } from '@/utils/appGetToken';
import { EU_SITE } from '@/constants';

const allTemp = {
    ...LowcompIndex,
    ...LowcompMember,
};

const EcoCredits: NextPage<any> = ({ isLogin, timeLine, status, debug, SEO, id, data }: any) => {
    const { t } = useLang('ecocredits');
    const { locale } = getLocal();
    const { isApp } = useGlobal();

    // Mark getAppMsg
    if (isApp) {
        const token = getAppSession();
        const dataSet = {
            token,
            user: {},
        };
        onLogin(dataSet);
    }

    const initState = {
        isLogin: isApp ? true : isLogin,
        timeLine: timeLine,
        pageStatus: status || IPageStatus.TEASER,
    };
    const isDebug = debug && Object.values(IPageStatus).includes(debug);
    if (isDebug) {
        initState.pageStatus = debug;
    }
    const IS_PROD: boolean = process.env.NEXT_PUBLIC_MODY === 'prd';
    const IS_FIX_SITES: boolean = ['sa'].includes(locale);

    return (
        <>
            <Head>
                {EU_SITE.includes(locale) && (
                    <meta
                        property="og:image"
                        content={
                            locale === 'uk'
                                ? 'https://websiteoss.ecoflow.com/upload/image/1747722456071/banner-UK.jpg'
                                : 'https://websiteoss.ecoflow.com/upload/image/1747722394476/banner-EU.jpg'
                        }
                    />
                )}
            </Head>

            <SEO title={t('seo_title') || t('join_title')} description={t('seo_desc')} titleTemplate={locale === 'jp' ? 'EcoFlow Japan' : ''} />
            <SEOlinksA locale={String(locale)} list={seoconfig.include} pathname={seoconfig.url} />
            <PageLayout isNoFixed>
                {IS_FIX_SITES && IS_PROD ? (
                    <MonthlyProvider state={initState}>
                        <EcoCreditsCompoment />
                    </MonthlyProvider>
                ) : (
                    <MemberTimeProvider>
                        <MemberGloData isOpenA={true} />
                        <EcoProvider>
                            <Product id={id} data={data} allTemp={allTemp} />
                        </EcoProvider>
                    </MemberTimeProvider>
                )}
            </PageLayout>
        </>
    );
};

export default EcoCredits;

// 以下两个方法不可删除，否则会重复触发 _app.js 中 getInitialProps 中的请求
export async function getServerSideProps({ locale, query, req }: GetServerSidePropsContext) {
    const blackLocaleList = ['cn', 'tw'];
    if (blackLocaleList.includes(locale as string)) {
        return redirectTo404();
    }
    // 包含 timeLine
    let ecocreditsData = null;
    try {
        ecocreditsData = require(`@/data/ecocredits/${locale}.js`);
    } catch (err) {
        ecocreditsData = require(`@/data/ecocredits/index.js`);
        throw new Error('读取/data/ecocredits/index.js失败');
    }
    // 时间节点判断 正式环境未开始重定向404
    const { debug } = query;
    const time = new Date().getTime();
    // if (process.env.NEXT_PUBLIC_MODY === 'prd') {
    //     if (time < ecocreditsData.timeLine?.teaser.start && !debug) {
    //         return redirectTo404();
    //     }
    //     debug = '';
    // }

    // 当前阶段
    let status: IPageStatus = IPageStatus.TEASER;
    if (ecocreditsData.timeLine) {
        if (time < ecocreditsData.timeLine.teaser.start) {
            status = IPageStatus.NOTSTARTED;
        } else if (time < ecocreditsData.timeLine.teaser.end) {
            status = IPageStatus.TEASER;
        } else if (time < ecocreditsData.timeLine.activity.end) {
            status = IPageStatus.ACTIVITY;
        } else {
            status = IPageStatus.END;
        }
    }

    // 获取登陆信息
    const userInfoJson = req.cookies?.userInfo || '';
    let isLogin = false;
    try {
        const userInfo = JSON.parse(userInfoJson);
        isLogin = !!userInfo.token;
    } catch {}

    // 配置化
    let data = {
        thePage: {},
        theTemp: [],
        theData: {},
        theAttr: {},
        sqlData: {},
    };
    const id = 'ecocredits';
    try {
        data = await queryEditorDetailOnline(id, locale as string);
    } catch (e: any) {}

    return {
        props: {
            isLogin,
            timeLine: ecocreditsData.timeLine,
            status,
            debug: debug || '',
            id,
            data,
        },
    };
}
